import { FC } from 'react';

import { AnyFCO, DatasetFCO, DatasetFCOFields, FCOFields } from '../../../core/types/fcoTypes';
import { useUserSettings } from '../../context/UserSettingsContext';
import { Card, ColumnLayout, Empty, ExtendedTableColumn, KeyValueList, Link, Loading, Table, Text } from '@lithos';
import { dateTimeFormat, timeZone } from '@lithos/DateUtils';

interface DatasetCardProps {
  fco: DatasetFCO;
  idToFcoMap: Record<string, AnyFCO>;
}

const DatasetMetadataCard: FC<DatasetCardProps> = ({ fco, idToFcoMap }) => {
  const { workspace } = useUserSettings();
  let source = <></>;

  if (fco[DatasetFCOFields.FEATURE_SERVICE_ID] !== undefined) {
    if (idToFcoMap[fco[DatasetFCOFields.FEATURE_SERVICE_ID]]) {
      const featureService = idToFcoMap[fco[DatasetFCOFields.FEATURE_SERVICE_ID]];
      source = (
        <>
          <Link
            url={`/repo/${workspace}/feature-services/${featureService.name}/dataflow`}
            icon="FeatureService"
            name={featureService.name ?? ''}
          ></Link>
        </>
      );
    } else {
      source = <Link disabled={true} name={fco[DatasetFCOFields.FEATURE_SERVICE_NAME] ?? ''} icon={'FeatureService'} />;
    }
  }

  if (fco[DatasetFCOFields.FEATURE_VIEW_ID] !== undefined) {
    if (idToFcoMap[fco[DatasetFCOFields.FEATURE_VIEW_ID]]) {
      const featureView = idToFcoMap[fco[DatasetFCOFields.FEATURE_VIEW_ID]];
      source = (
        <>
          <Link
            url={`/repo/${workspace}/features/${featureView.name}/dataflow`}
            icon="FeatureView"
            name={featureView.name ?? ''}
          ></Link>
        </>
      );
    } else {
      source = <Link disabled={true} name={fco[DatasetFCOFields.FEATURE_VIEW_NAME] ?? ''} icon={'FeatureView'} />;
    }
  }

  const items = [
    {
      title: <>Type</>,
      content: fco[DatasetFCOFields.TYPE],
    },
    {
      title: <>Input</>,
      content: source,
    },
    {
      title: <>Created</>,
      content: fco[FCOFields.CREATED_DATE] ? dateTimeFormat(fco[FCOFields.CREATED_DATE], timeZone.UTC) : <>-</>,
    },
    {
      title: <>Commit ID</>,
      content: <Text code>{fco[DatasetFCOFields.STATE_UPDATE_ENTRY_COMMIT_ID]}</Text>,
    },
  ];

  return (
    <Card title="Metadata">
      <KeyValueList items={items} />
    </Card>
  );
};

const DatasetSchemaCard: FC<DatasetCardProps> = ({ fco }) => {
  interface SchemaItem {
    title: React.ReactNode;
    content: React.ReactNode;
  }

  const columns: ExtendedTableColumn<SchemaItem & { id: string }>[] = [
    {
      name: 'Name',
      content: (item: SchemaItem) => {
        return item.title;
      },
    },
    {
      name: 'Type',
      content: (item: SchemaItem) => {
        return (
          <Text code>
            <>{item.content}</>
          </Text>
        );
      },
    },
  ];

  let items: (SchemaItem & { id: string })[];

  if (fco[DatasetFCOFields.UNIFIED_SCHEMA]) {
    // New Schema, post Tecton 1.0
    items = fco[DatasetFCOFields.UNIFIED_SCHEMA].map((schemaItem) => {
      return {
        id: schemaItem.name,
        title: <>{schemaItem.name}</>,
        content: <Text code>{schemaItem.type}</Text>,
      };
    });
  } else {
    // Legacy Schema
    items = fco[DatasetFCOFields.SCHEMA].map((schemaItem) => {
      return {
        id: schemaItem.name,
        title: <>{schemaItem.name}</>,
        content: <Text code>{schemaItem.type}</Text>,
      };
    });
  }

  return (
    <Card title="Schema">
      <Table columns={columns} items={items} />
    </Card>
  );
};

interface DatasetOverviewProps {
  fco?: DatasetFCO;
  idToFcoMap: Record<string, AnyFCO>;
  isLoading?: boolean;
  isError?: boolean;
}

const DataSetOverview: FC<DatasetOverviewProps> = ({ fco, idToFcoMap, isError, isLoading }) => {
  if (isLoading) {
    return <Loading />;
  }

  if (isError || !fco) {
    return (
      <Empty
        useCase="errorUnknown"
        title={'Error Loading Dataset'}
        description={<>We ran into an error loading this dataset.</>}
        actions={undefined}
        placement="full-page"
      />
    );
  }
  return (
    <ColumnLayout>
      <DatasetMetadataCard fco={fco!} idToFcoMap={idToFcoMap} />
      <DatasetSchemaCard fco={fco!} idToFcoMap={idToFcoMap} />
    </ColumnLayout>
  );
};

export default DataSetOverview;
